import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { AuthAPI } from '@/api'
import type { AuthLoginReq, StaffData } from '@/api/types'
export const useStaffStore = defineStore('staff', () => {
  // 状态
  const staffData = ref<StaffData | null>(null)
  const token = ref<string>('')
  const refreshToken = ref<string>('')

  // 计算属性
  const isLoggedIn = computed(() => !!token.value)
  const isSuperUser = computed(() => staffData.value?.info?.is_super_admin || false)
  const staffName = computed(() => {
    if (!staffData.value) return ''
    return staffData.value.info?.nickname || staffData.value.info?.username
  })

  // 从 localStorage 恢复状态
  const restoreAuth = () => {
    const storedToken = localStorage.getItem('access_token')
    const storedRefreshToken = localStorage.getItem('refresh_token')
    const storedStaffData = localStorage.getItem('staff_data')

    if (storedToken) {
      token.value = storedToken
    }
    if (storedRefreshToken) {
      refreshToken.value = storedRefreshToken
    }
    if (storedStaffData) {
      try {
        staffData.value = JSON.parse(storedStaffData)
      } catch (error) {
        console.error('Failed to parse staff data:', error)
      }
    }
  }

  // 登录
  const login = async (loginData: AuthLoginReq): Promise<void> => {
    const res = await AuthAPI.login(loginData)

    // 保存 token 和员工信息
    token.value = res.data.access_token || ''
    refreshToken.value = res.data.refresh_token || ''
    staffData.value = res.data.staff || null

    // 保存到 localStorage
    localStorage.setItem('access_token', res.data.access_token || '')
    localStorage.setItem('refresh_token', res.data.refresh_token || '')
    localStorage.setItem('staff_data', JSON.stringify(res.data.staff || null))
  }

  // 登出
  const logout = async (): Promise<void> => {
    try {
      // 调用登出接口
      await AuthAPI.logout({})
    } catch (error) {
      console.error('Logout API error:', error)
    } finally {
      // 清除状态
      clearAuth()
    }
  }

  // 清除认证信息
  const clearAuth = () => {
    token.value = ''
    refreshToken.value = ''
    staffData.value = null

    // 清除 localStorage
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('staff_data')
  }

  // 刷新 token
  const refreshAccessToken = async (): Promise<void> => {
    try {
      const res = await AuthAPI.refresh({ refresh_token: refreshToken.value })

      // 更新 token
      token.value = res.data.access_token || ''
      refreshToken.value = res.data.refresh_token || ''

      // 更新 localStorage
      localStorage.setItem('access_token', res.data.access_token || '')
      localStorage.setItem('refresh_token', res.data.refresh_token || '')
    } catch (error) {
      // 刷新失败，清除认证信息
      clearAuth()
      throw error
    }
  }

  // 更新员工信息
  const updateStaffInfo = (data: Partial<StaffData>) => {
    if (staffData.value) {
      staffData.value = { ...staffData.value, ...data }
      localStorage.setItem('staff_data', JSON.stringify(staffData.value))
    }
  }

  // 检查权限
  const hasPermission = (permission: string): boolean => {
    if (isSuperUser.value) return true
    // TODO: 实现权限检查逻辑
    console.log('Checking permission:', permission)
    return false
  }

  // 检查角色
  const hasRole = (roleCode: string): boolean => {
    if (isSuperUser.value) return true
    return staffData.value?.roles?.some((role) => role.code === roleCode) || false
  }

  // 初始化时恢复认证状态
  restoreAuth()

  return {
    // 状态
    staffData,
    token,
    refreshToken,
    // 计算属性
    isLoggedIn,
    isSuperUser,
    staffName,
    // 方法
    login,
    logout,
    clearAuth,
    refreshAccessToken,
    updateStaffInfo,
    hasPermission,
    hasRole,
  }
})
